import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { FiShoppingCart, FiUser, FiMenu, FiX, FiLogOut, FiPackage, FiSearch } from 'react-icons/fi';
import { IAppDispatch, IRootState } from '../../../redux/store';
import { signOut } from '../../../redux/api/authSlice';
import { resetCartItems } from '../../../redux/userApi/cart/cartSlice';
import FooterNew from '../footer-new/FooterNew';
import './NavbarNew.css';
import MegaMenu from './MegaMenu';
import { Link } from 'react-router-dom';
import Logo from '../../../assets/images/The Pickers Stop LOGO.png'
import { categoryData } from '../../../constant/categoryData';
import { fetchProducts } from '../../../redux/products/productsSlice';

const NavbarNew: React.FC = () => {
    const dispatch = useDispatch<IAppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();

    // State
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
    const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    const userDropdownRef = React.useRef<HTMLDivElement>(null);
    const searchDropdownRef = React.useRef<HTMLDivElement>(null);

    // Redux selectors
    const cartItems = useSelector((state: IRootState) => state.cart.items) || [];
    const products = useSelector((state: IRootState) => state.products.items) || [];
    const { accessToken, userData, role } = useSelector((state: IRootState) => state.auth);
    const isAuthenticated = !!accessToken;

    const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);

    // Navigation Data
    // Build navCategories dynamically: Main categories from categoryData, Subcategories from Redux store (if exists, else categoryData)
    const categoryDataMap = useSelector((state: IRootState) => state.categories.data) || {};
    const navCategories = Object.keys(categoryData).map((catName) => {
        const subcategories = categoryDataMap[catName] || categoryData[catName] || [];
        return {
            name: catName,
            items: subcategories.map((sub: string) => ({
                label: sub,
                link: `/shop?category=${encodeURIComponent(catName)}&sub=${encodeURIComponent(sub)}`,
            })),
        };
    });

    // Fetch products when search opens and products list is empty
    useEffect(() => {
        if (isSearchOpen && products.length === 0) {
            dispatch(fetchProducts() as any);
        }
    }, [isSearchOpen, products.length, dispatch]);

    // Handle Escape key to close search bar
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsSearchOpen(false);
                setSearchQuery('');
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // Toggle scroll state & handle click outside dropdowns
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };
        const handleClickOutside = (event: MouseEvent) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
                setIsUserDropdownOpen(false);
            }
            if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target as Node)) {
                setIsSearchOpen(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handle logout
    const handleLogout = () => {
        setIsUserDropdownOpen(false);
        dispatch(signOut());
        dispatch(resetCartItems());
        // Small delay to ensure Redux state updates before navigation
        setTimeout(() => {
            navigate('/');
        }, 100);
    };

    // Close mobile menu and search on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsSearchOpen(false);
        setSearchQuery('');
    }, [location]);

    // Search matching logic
    const getSearchResults = () => {
        if (!searchQuery.trim()) {
            return { matchedCategories: [], matchedProducts: [] };
        }
        const query = searchQuery.toLowerCase();

        // 1. Match subcategories/categories
        const matchedCats: Array<{ subCategory: string; category: string }> = [];
        Object.entries(categoryData).forEach(([category, subcategories]) => {
            if (category.toLowerCase().includes(query)) {
                subcategories.slice(0, 3).forEach(sub => {
                    matchedCats.push({ subCategory: sub, category });
                });
            } else {
                subcategories.forEach(sub => {
                    if (sub.toLowerCase().includes(query)) {
                        matchedCats.push({ subCategory: sub, category });
                    }
                });
            }
        });

        // 2. Match products
        const matchedProds = products.filter(product => {
            return (
                product.name.toLowerCase().includes(query) ||
                product.category.toLowerCase().includes(query) ||
                (product.sub_category && product.sub_category.toLowerCase().includes(query)) ||
                product.description.toLowerCase().includes(query)
            );
        });

        return {
            matchedCategories: matchedCats.slice(0, 5),
            matchedProducts: matchedProds.slice(0, 6)
        };
    };

    const { matchedCategories, matchedProducts } = getSearchResults();

    return (
        <>
            <header className={`navbar-new sticky ${isScrolled ? 'scrolled' : ''}`}>
                <div className="container navbar-container">
                    {/* Logo */}
                    <div className="navbar-logo">
                        <Link to="/">
                            <img src={Logo} alt="logo" style={{ maxHeight: '60px', width: 'auto', padding: '5px 0' }} />
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <nav className="navbar-links desktop-only" onMouseLeave={() => setActiveMegaMenu(null)}>
                        {navCategories.map((cat) => (
                            <div
                                key={cat.name}
                                className="nav-item-wrapper"
                                onMouseEnter={() => setActiveMegaMenu(cat.name)}
                            >
                                <NavLink
                                    to={`/shop?category=${cat.name}`}
                                    className={({ isActive }) => {
                                        // Don't mark as active if we're on /shop without a category parameter
                                        const searchParams = new URLSearchParams(location.search);
                                        const currentCategory = searchParams.get('category');

                                        // Only mark active if the current category matches this nav item
                                        if (location.pathname === '/shop' && currentCategory === cat.name) {
                                            return 'nav-link active';
                                        }
                                        return 'nav-link';
                                    }}
                                >
                                    {cat.name}
                                </NavLink>
                                <AnimatePresence>
                                    {activeMegaMenu === cat.name && (
                                        <MegaMenu
                                            category={cat.name}
                                            items={cat.items}
                                            // images={cat.images}
                                            onClose={() => setActiveMegaMenu(null)}
                                        />
                                    )}
                                </AnimatePresence>
                            </div>
                        ))}
                    </nav>

                    {/* Icons / Actions */}
                    <div className="navbar-actions">
                        {/* Premium Searchbar dropdown trigger and popover */}
                        <div className="search-dropdown-wrapper" ref={searchDropdownRef}>
                            <button
                                className={`icon-btn search-trigger-btn ${isSearchOpen ? 'active' : ''}`}
                                onClick={() => {
                                    setIsSearchOpen(!isSearchOpen);
                                    if (!isSearchOpen) {
                                        setTimeout(() => {
                                            document.getElementById('header-search-input')?.focus();
                                        }, 100);
                                    }
                                }}
                                aria-label="Search products"
                            >
                                <FiSearch />
                            </button>

                            <AnimatePresence>
                                {isSearchOpen && (
                                    <motion.div
                                        className="search-dropdown-menu"
                                        initial={{ opacity: 0, y: 15, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 15, scale: 0.95 }}
                                        transition={{ duration: 0.2, ease: 'easeOut' }}
                                    >
                                        {/* Search Input Container */}
                                        <div className="search-input-container">
                                            <FiSearch className="search-input-icon" />
                                            <input
                                                id="header-search-input"
                                                type="text"
                                                className="search-input-field"
                                                placeholder="Search products, categories..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                autoComplete="off"
                                            />
                                            {searchQuery && (
                                                <button
                                                    className="search-clear-btn"
                                                    onClick={() => {
                                                        setSearchQuery('');
                                                        document.getElementById('header-search-input')?.focus();
                                                    }}
                                                    aria-label="Clear search"
                                                >
                                                    <FiX />
                                                </button>
                                            )}
                                        </div>

                                        {/* Search Results Area */}
                                        <div className="search-results-scrollable">
                                            {searchQuery.trim() === '' ? (
                                                <div className="search-suggestion-hint">
                                                    <p className="hint-title">Popular Searches</p>
                                                    <div className="popular-tags">
                                                        {['Table', 'Chair', 'Sofa', 'Laptop', 'Clock', 'Paper'].map(tag => (
                                                            <button
                                                                key={tag}
                                                                className="popular-tag-btn"
                                                                onClick={() => {
                                                                    setSearchQuery(tag);
                                                                    setTimeout(() => {
                                                                        document.getElementById('header-search-input')?.focus();
                                                                    }, 50);
                                                                }}
                                                            >
                                                                {tag}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {/* Categories Section */}
                                                    {matchedCategories.length > 0 && (
                                                        <div className="search-section">
                                                            <h5 className="search-section-title">Categories</h5>
                                                            <div className="search-categories-list">
                                                                {matchedCategories.map((cat, idx) => (
                                                                    <Link
                                                                        key={`${cat.subCategory}-${idx}`}
                                                                        to={`/shop?category=${encodeURIComponent(cat.category)}&sub=${encodeURIComponent(cat.subCategory)}`}
                                                                        className="search-category-item"
                                                                        onClick={() => setIsSearchOpen(false)}
                                                                    >
                                                                        <span className="category-name">{cat.subCategory}</span>
                                                                        <span className="category-action">
                                                                            Shop by {cat.category}
                                                                        </span>
                                                                    </Link>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Products Section */}
                                                    {matchedProducts.length > 0 && (
                                                        <div className="search-section">
                                                            <h5 className="search-section-title">Products</h5>
                                                            <div className="search-products-list">
                                                                {matchedProducts.map((product) => {
                                                                    const imageUrl = Array.isArray(product.image_dir)
                                                                        ? product.image_dir[0]
                                                                        : product.image_dir || '';
                                                                    return (
                                                                        <Link
                                                                            key={product.id}
                                                                            to={`/products/${product.id}`}
                                                                            className="search-product-item"
                                                                            onClick={() => setIsSearchOpen(false)}
                                                                        >
                                                                            <div className="product-thumb-wrapper">
                                                                                {imageUrl ? (
                                                                                    <img src={imageUrl} alt={product.name} className="product-thumb" />
                                                                                ) : (
                                                                                    <div className="product-thumb-placeholder">?</div>
                                                                                )}
                                                                            </div>
                                                                            <div className="product-info">
                                                                                <span className="product-title">{product.name}</span>
                                                                                <span className="product-price">₹{Number(product.price).toFixed(2)}</span>
                                                                            </div>
                                                                        </Link>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Empty State */}
                                                    {matchedCategories.length === 0 && matchedProducts.length === 0 && (
                                                        <div className="search-no-results">
                                                            <p>No results found for "{searchQuery}"</p>
                                                            <span>Try searching for something else</span>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {isAuthenticated ? (
                            <div className="user-dropdown-wrapper" ref={userDropdownRef}>
                                <button
                                    className={`icon-btn user-trigger-btn ${isUserDropdownOpen ? 'active' : ''}`}
                                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                                    aria-label="User menu"
                                >
                                    <span className="user-icon-wrapper">
                                        <FiUser />
                                        <span className="online-dot"></span>
                                    </span>
                                    {userData?.first_name && (
                                        <span className="user-greeting">Hello, {userData.first_name}</span>
                                    )}
                                </button>
                                <AnimatePresence>
                                    {isUserDropdownOpen && (
                                        <motion.div
                                            className="user-dropdown-menu"
                                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <div className="user-dropdown-header">
                                                <p className="user-name premium-name">{userData?.first_name ? `${userData.first_name} ${userData.last_name || ''}`.trim() : 'My Account'}</p>
                                            </div>
                                            <div className="user-dropdown-divider"></div>
                                            <div className="user-dropdown-links">
                                                <NavLink to="/profile" className="dropdown-link" onClick={() => setIsUserDropdownOpen(false)}>
                                                    <FiUser />  Profile
                                                </NavLink>
                                                {(role === 'vendor' || role === 'admin') && (
                                                    <NavLink to="/vendor/products" className="dropdown-link" onClick={() => setIsUserDropdownOpen(false)}>
                                                        <FiPackage /> My Products
                                                    </NavLink>
                                                )}
                                                {role === 'admin' && (
                                                    <NavLink to="/admin" className="dropdown-link admin-link" onClick={() => setIsUserDropdownOpen(false)}>
                                                        Admin Dashboard
                                                    </NavLink>
                                                )}
                                                <button className="dropdown-link logout-btn" onClick={() => { handleLogout(); setIsUserDropdownOpen(false); }}>
                                                    <FiLogOut /> Logout
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ) : (
                            <NavLink to="/signIn" className="icon-btn" aria-label="Sign In">
                                <FiUser />
                            </NavLink>
                        )}

                        <NavLink to="/cart" className="icon-btn cart-btn" aria-label="Cart">
                            <FiShoppingCart />
                            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
                        </NavLink>

                        {/* Mobile Menu Toggle */}
                        <button
                            className="icon-btn mobile-menu-toggle"
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        >
                            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu Overlay */}
                <AnimatePresence>
                    {isMobileMenuOpen && (
                        <motion.div
                            className="mobile-menu"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                        >
                            <nav className="mobile-nav">
                                {navCategories.map((cat) => (
                                    <div key={cat.name} className="mobile-nav-item">
                                        <div
                                            className="mobile-nav-header"
                                            onClick={() => setMobileExpanded(mobileExpanded === cat.name ? null : cat.name)}
                                        >
                                            <span>{cat.name}</span>
                                            <span>{mobileExpanded === cat.name ? '-' : '+'}</span>
                                        </div>
                                        <AnimatePresence>
                                            {mobileExpanded === cat.name && (
                                                <motion.div
                                                    className="mobile-nav-sub"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                >
                                                    {cat.items.map((item: any) => (
                                                        <NavLink
                                                            key={item.label}
                                                            to={item.link}
                                                            className="mobile-sub-link"
                                                            onClick={() => setIsMobileMenuOpen(false)}
                                                        >
                                                            {item.label}
                                                        </NavLink>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                                {isAuthenticated ? (
                                    <>
                                        <NavLink to="/profile" className="mobile-link" onClick={() => setIsMobileMenuOpen(false)}>Profile</NavLink>
                                        {(role === 'vendor' || role === 'admin') && (
                                            <NavLink to="/vendor/products" className="mobile-link" onClick={() => setIsMobileMenuOpen(false)}>Product Management</NavLink>
                                        )}
                                        {role === 'admin' && (
                                            <NavLink to="/admin" className="mobile-link admin-mobile" onClick={() => setIsMobileMenuOpen(false)}>Admin Dashboard</NavLink>
                                        )}
                                        <button className="mobile-link logout-mobile" onClick={handleLogout}>Logout</button>
                                    </>
                                ) : (
                                    <NavLink to="/signIn" className="mobile-link" onClick={() => setIsMobileMenuOpen(false)}>Sign In</NavLink>
                                )}
                            </nav>
                        </motion.div>
                    )}
                </AnimatePresence>
            </header>

            <Outlet />
            <FooterNew />
        </>
    );
};

export default NavbarNew;
